// UserManagement utilities

import { User } from '@/api/types'
import { Tenant } from '@/stores/tenantStore'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import _ from 'lodash'
import type { TenantOption } from '../types'

// Transform context tenants to include Gravitate first
export const transformTenants = (tenants: Tenant[]): TenantOption[] => {
  return [
    {
      name: 'Gravitate',
      id: 'gravitate',
      description: 'Master access to all tenants',
    },
    ...tenants
      .filter((t) => t.name !== 'Gravitate')
      .map((t) => ({
        name: t.name,
        id: t.id,
        description: `${t.name} tenant access`,
      })),
  ]
}

// Generate column definitions for user grid
export const getColumnDefinitions = (
  ActiveSwitchRenderer: React.ComponentType<ICellRendererParams>,
  DomainsRenderer: React.ComponentType<ICellRendererParams>,
  EmailVerifiedRenderer: React.ComponentType<ICellRendererParams>,
  ActionsRenderer: React.ComponentType<ICellRendererParams>,
): ColDef<User>[] => [
  {
    field: 'is_active',
    headerName: 'Active',
    cellRenderer: ActiveSwitchRenderer,
    width: 100,
    maxWidth: 110,
    pinned: 'left',
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
    editable: false,
  },
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    filter: true,
    minWidth: 180,
    flex: 1.2,
    editable: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    sortable: true,
    filter: true,
    minWidth: 220,
    flex: 1.4,
    editable: false,
  },
  {
    field: 'domains',
    headerName: 'Domains',
    cellRenderer: DomainsRenderer,
    sortable: false,
    filter: false,
    flex: 2,
    editable: false,
    autoHeight: true,
    wrapText: true,
  },
  {
    field: 'email_confirmed',
    headerName: 'Email Verified',
    sortable: true,
    filter: true,
    flex: 1,
    cellRenderer: EmailVerifiedRenderer,
    editable: false,
  },
  {
    headerName: 'Actions',
    cellRenderer: ActionsRenderer,
    sortable: false,
    filter: false,
    pinned: 'right',
    editable: false,
    width: 450,
    minWidth: 450,
    maxWidth: 450,
    flex: 0,
  },
]

// Present role name with leading capital for display
export const formatRoleName = (role: string | undefined | null): string => {
  if (!role) return ''

  if (/^[0-9a-fA-F]{16,}$/.test(role)) {
    // Looks like an ID; return as-is
    return role
  }

  return _.startCase(role || '')
}
